import React from "react";
import DynamicFormCard from "../forms/DynamicFormCard";
import ReviewTaskCard from "../forms/ReviewTaskCard";
import CreatedFileCard from "../files/CreatedFileCard";
import {
  isDynamicFormTool,
  isCreatedFileTool,
  parseMaybeJson,
} from "./conversationUtils";
import { SendMessageCard } from "./MessageBubbles";
import {
  ScheduleMessageCard,
  RecurringScheduleCard,
  ToolCallCard,
} from "./ToolCards";
import SpawnWorkerCard from "./SpawnWorkerCard";
import AskHumanCard from "./AskHumanCard";
import CredentialSetupCard from "./CredentialSetupCard";
import HumanGuideCard from "./HumanGuideCard";
import ArtifactToolCard, { canRenderArtifactToolCard } from "./ArtifactToolCard";

/**
 * Renders the rich inline card for a tool part (forms, files, schedules, HITL, etc.).
 * Shared by the normal tool path and the post-approval executed-result path so gated tools
 * never fall back to a raw ToolCallCard when a specialized card exists.
 */
export function renderSpecializedToolCard({
  partKey,
  toolName,
  inputStr,
  resultStr,
  parsedInput = null,
  parsedOutput = null,
  lifecycleState = "output-available",
  grouped = false,
  agentName,
  agentId,
  chatSessionId,
  messageTimestamp = null,
  onOpenAgentSettings,
  onOpenDynamicForm,
  onOpenCreatedFile,
  respondedCorrelationIds,
  scheduledMessages,
  onEditScheduledMessage,
  onCancelScheduledMessage,
  onRefreshScheduledMessages,
  compactToolCalls,
  workerStatusMap,
  onSubmitAskHumanAnswer,
  onSubmitCredentialSetup,
}) {
  if (isDynamicFormTool(toolName)) {
    return (
      <DynamicFormCard
        key={partKey}
        toolName={toolName}
        args={inputStr}
        result={resultStr}
        agentName={agentName}
        agentId={agentId}
        grouped={grouped}
        onOpenForm={onOpenDynamicForm}
        onOpenAgentSettings={onOpenAgentSettings}
        lifecycleState={lifecycleState}
      />
    );
  }

  if (toolName === "review_task") {
    return (
      <ReviewTaskCard
        key={partKey}
        args={inputStr}
        result={resultStr}
        agentName={agentName}
        agentId={agentId}
        grouped={grouped}
        onOpenForm={onOpenDynamicForm}
        onOpenAgentSettings={onOpenAgentSettings}
        lifecycleState={lifecycleState}
      />
    );
  }

  if (isCreatedFileTool(toolName)) {
    return (
      <CreatedFileCard
        key={partKey}
        toolName={toolName}
        args={inputStr}
        result={resultStr}
        agentName={agentName}
        agentId={agentId}
        grouped={grouped}
        onOpenFile={onOpenCreatedFile}
        onOpenAgentSettings={onOpenAgentSettings}
        lifecycleState={lifecycleState}
      />
    );
  }

  if (toolName === "send_message") {
    let sendMsgResponseReq = null;
    let sendMsgCorrelationId = null;
    if (parsedInput && typeof parsedInput === "object") {
      sendMsgResponseReq = parsedInput.responseRequirement;
    }
    const outputObj = parsedOutput;
    if (outputObj && typeof outputObj === "object") {
      if (!sendMsgResponseReq) sendMsgResponseReq = outputObj.responseRequirement;
      sendMsgCorrelationId = outputObj.correlationId;
    }
    const awaitingResponse =
      sendMsgResponseReq === "REQUIRED" &&
      sendMsgCorrelationId &&
      respondedCorrelationIds &&
      !respondedCorrelationIds.has(sendMsgCorrelationId);
    return (
      <SendMessageCard
        key={partKey}
        args={inputStr}
        result={resultStr}
        agentName={agentName}
        agentId={agentId}
        grouped={grouped}
        awaitingResponse={awaitingResponse}
        onOpenAgentSettings={onOpenAgentSettings}
        lifecycleState={lifecycleState}
      />
    );
  }

  if (toolName === "schedule_message") {
    return (
      <ScheduleMessageCard
        key={partKey}
        args={inputStr}
        result={resultStr}
        agentName={agentName}
        agentId={agentId}
        grouped={grouped}
        timestamp={messageTimestamp}
        lifecycleState={lifecycleState}
        scheduledMessages={scheduledMessages}
        onEditScheduledMessage={onEditScheduledMessage}
        onCancelScheduledMessage={onCancelScheduledMessage}
        onRefreshScheduledMessages={onRefreshScheduledMessages}
      />
    );
  }

  if (toolName === "recurring_schedule") {
    return (
      <RecurringScheduleCard
        key={partKey}
        args={inputStr}
        result={resultStr}
        agentName={agentName}
        agentId={agentId}
        grouped={grouped}
        lifecycleState={lifecycleState}
      />
    );
  }

  if (toolName === "spawn_worker") {
    let workerLiveStatus = null;
    if (workerStatusMap) {
      try {
        const parsed =
          typeof resultStr === "string" ? JSON.parse(resultStr) : resultStr;
        if (
          parsed?.workerSessionId &&
          workerStatusMap[parsed.workerSessionId]
        ) {
          workerLiveStatus = workerStatusMap[parsed.workerSessionId].status;
        }
      } catch {}
    }
    return (
      <SpawnWorkerCard
        key={partKey}
        args={inputStr}
        result={resultStr}
        agentName={agentName}
        agentId={agentId}
        grouped={grouped}
        onOpenAgentSettings={onOpenAgentSettings}
        lifecycleState={lifecycleState}
        compact={compactToolCalls}
        liveWorkerStatus={workerLiveStatus}
      />
    );
  }

  if (toolName === "ask_human") {
    return (
      <AskHumanCard
        key={partKey}
        args={inputStr}
        result={resultStr}
        lifecycleState={lifecycleState}
        agentName={agentName}
        agentId={agentId}
        grouped={grouped}
        chatSessionId={chatSessionId}
        onSubmitAnswer={onSubmitAskHumanAnswer}
        onOpenAgentSettings={onOpenAgentSettings}
      />
    );
  }

  if (toolName === "request_credential_setup") {
    return (
      <CredentialSetupCard
        key={partKey}
        args={inputStr}
        result={resultStr}
        agentName={agentName}
        agentId={agentId}
        grouped={grouped}
        chatSessionId={chatSessionId}
        onSubmitCredentialSetup={onSubmitCredentialSetup}
        onOpenAgentSettings={onOpenAgentSettings}
      />
    );
  }

  if (toolName === "guide_human") {
    return (
      <HumanGuideCard
        key={partKey}
        args={inputStr}
        result={resultStr}
        agentName={agentName}
        agentId={agentId}
        grouped={grouped}
        chatSessionId={chatSessionId}
        onOpenAgentSettings={onOpenAgentSettings}
      />
    );
  }

  if (toolName === "create_artifact") {
    if (canRenderArtifactToolCard(resultStr)) {
      return (
        <ArtifactToolCard
          key={partKey}
          args={inputStr}
          result={resultStr}
          agentName={agentName}
          agentId={agentId}
          grouped={grouped}
          onOpenAgentSettings={onOpenAgentSettings}
        />
      );
    }
  }

  return (
    <ToolCallCard
      key={partKey}
      toolName={toolName}
      args={inputStr}
      result={resultStr}
      agentName={agentName}
      agentId={agentId}
      grouped={grouped}
      onOpenAgentSettings={onOpenAgentSettings}
      lifecycleState={lifecycleState}
      compact={compactToolCalls}
    />
  );
}

/** Build the shared props bag for {@link renderSpecializedToolCard}. */
export function buildSpecializedToolCardContext({
  partKey,
  toolName,
  inputStr,
  resultStr,
  parsedInput,
  parsedOutput,
  lifecycleState,
  grouped,
  typedMessage,
  opts,
}) {
  return {
    partKey,
    toolName,
    inputStr,
    resultStr,
    parsedInput,
    parsedOutput: parsedOutput ?? parseMaybeJson(resultStr),
    lifecycleState,
    grouped,
    agentName: opts.agentName,
    agentId: opts.agentId,
    chatSessionId: opts.chatSessionId,
    messageTimestamp:
      typedMessage?.timestamp ||
      typedMessage?.createdAt ||
      typedMessage?.updatedAt ||
      null,
    onOpenAgentSettings: opts.onOpenAgentSettings,
    onOpenDynamicForm: opts.onOpenDynamicForm,
    onOpenCreatedFile: opts.onOpenCreatedFile,
    respondedCorrelationIds: opts.respondedCorrelationIds,
    scheduledMessages: opts.scheduledMessages,
    onEditScheduledMessage: opts.onEditScheduledMessage,
    onCancelScheduledMessage: opts.onCancelScheduledMessage,
    onRefreshScheduledMessages: opts.onRefreshScheduledMessages,
    compactToolCalls: opts.compactToolCalls,
    workerStatusMap: opts.workerStatusMap,
    onSubmitAskHumanAnswer: opts.onSubmitAskHumanAnswer,
    onSubmitCredentialSetup: opts.onSubmitCredentialSetup,
  };
}
